/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import _ from "lodash";
import { showErrorAction } from "../../error/errorActionCreator";
import { getEntitiesAttributesForPolicy, getResourceNSApi, getResourcesApi, getDPAttributesForPolicy, getDPModelApi, getDPSecurableArrtibutes, getRemoteUrlDataResult } from '../../../services/policy';
import { hideLoader, showLoader } from "../../loader/loader";
import { GET_ENTITIES_ATTRIBUTES_DATA_ERROR, GET_ENTITIES_NAMESPACE_DATA_ERROR, GET_ENTITIES_RESOURCE_DATA_ERROR, GET_ENTITIES_DATA_MODEL_ERROR, GET_SECURABLE_ATTRIBUTES_DATA_ERROR } from "../../../shared/PolicyDialog/constants";
import interpolateString from '../../../utility/stringInterpolation';
import { setEntitiesAttributesList, setResourceNS, setResources, setDPModel, setEntitiesAttributesRemoteDataUrlObject, setRemoteDataAttributes, resetRemoteUrlResponseData } from "./entityActionCreator";
import { isEmpty } from "../../../utility/isEmpty";
import jp from 'jsonpath';

/**
 * Get all the attributes based on the selection of the entity name and model verison
 * @param dispatch - Disptach is used to dispatch the action to set the values
 * @param namespaceId - Selected namespaceId
 * @param id - Selected id  
 * @param name - Selected name 
 */
export async function getEntitiesAttributes(dispatch: Function, namespaceId: number, id: number, name: string) {
  let attributes: any[] = [];
  let remoteDataUrlObject: Object = {};
  let errorMessage = { info: "", message: '' };
  dispatch(showLoader);
  dispatch(showErrorAction(errorMessage));
  try {
    if (id || name) {
      const { resourceSchema = "" } = await getEntitiesAttributesForPolicy(namespaceId, id, name);
      const attributeList = JSON.parse(resourceSchema);
      if (attributeList.definitions) {
        const finalAttributes = getFlattenedAttributes(attributeList);
        remoteDataUrlObject = getRemoteDataUrl(attributeList);
        if (isEmpty(remoteDataUrlObject)) {
          dispatch(resetRemoteUrlResponseData(false));
        }
        attributes = Object.entries(finalAttributes).map((mappedItem) => {
          return {
            key: mappedItem[0],
            displayName: mappedItem[0],
            name: mappedItem[0],
            value: mappedItem[1]
          }
        });
      }
      else {
        /**
        * Filters only securable attributes from list of entities attributes
        */
        const finalAttributes = attributeList;
        const { examples } = finalAttributes;
        attributes = Object.entries(examples[0][0]).filter((mappedItem: [string, any]) => mappedItem[1].securable).map((mappedItem: [string, any]) => {
          return {
            key: mappedItem[0],
            displayName: mappedItem[0],
            name: mappedItem[0],
            value: mappedItem[0]
          }
        });
      }
    } else {
      attributes = [];
      remoteDataUrlObject = {};
    }
  } catch (error: any) {
    if (error?.message === GET_SECURABLE_ATTRIBUTES_DATA_ERROR) {
      errorMessage = {
        info: error.message,
        message: '',
      };
    }
    else {
      errorMessage = {
        info: 'Resource ' + _.capitalize(name) + GET_ENTITIES_ATTRIBUTES_DATA_ERROR,
        message: "",
      };
    }
  }
  finally {
    dispatch(setEntitiesAttributesList(attributes));
    dispatch(setEntitiesAttributesRemoteDataUrlObject(remoteDataUrlObject));
    dispatch(hideLoader);
    dispatch(showErrorAction(errorMessage));
  }
}
export async function getDPEntitiesAttributes(dispatch: Function, entityName: string, modelVersion: string) {
  let attributes: any[] = [];
  let errorMessage = { info: '', message: '' };
  dispatch(showLoader);
  dispatch(showErrorAction(errorMessage));
  try {
    let attributeList = await getDPAttributesForPolicy(entityName, modelVersion);
    const dPSecurableArrtibutes = await getDPSecurableArrtibutes(entityName, modelVersion);
    const finalAttributes = getFlattenedAttributes(attributeList, dPSecurableArrtibutes);
    /**
   * Filters only securable attributes from list of entities attributes
   */
    attributes = Object.entries(finalAttributes).map((mappedItem) => {
      return {
        key: mappedItem[0],
        displayName: mappedItem[0],
        name: mappedItem[0],
        value: mappedItem[1]
      }
    });
  } catch (error: any) {
    if (error?.message === GET_SECURABLE_ATTRIBUTES_DATA_ERROR) {
      errorMessage = {
        info: error.message,
        message: '',
      };
    }
    else {
      errorMessage = {
        info: 'Resource ' + _.capitalize(entityName) + GET_ENTITIES_ATTRIBUTES_DATA_ERROR,
        message: "",
      };
    }

  }
  finally {
    dispatch(setEntitiesAttributesList(attributes));
    dispatch(hideLoader);
    dispatch(showErrorAction(errorMessage));
  }
}
const getFlattenedAttributes = (attributeList: any, dPSecurableArrtibutes?: any) => {
  const allDefinitions = attributeList.definitions;
  const finalAttributes = {};
  const attributeType = attributeList.items['$ref'].split('/')[2];
  const actualType = allDefinitions[attributeType];
  if (actualType['x-by-securable']) {
    if (dPSecurableArrtibutes?.dataSecurable) {
      getSecurableAttributesList(actualType, allDefinitions, finalAttributes, dPSecurableArrtibutes?.dataSecurable);
    } else {
      flattenAttributes(actualType, allDefinitions, finalAttributes, '');
    }
  } else {
    throw new Error(GET_SECURABLE_ATTRIBUTES_DATA_ERROR);
  }
  return finalAttributes;
}
/**
 * Get list of Namespace
 * @param dispatch - Disptach is used to dispatch the action to set the values
 */
export const getResourceNS = async (dispatch: Function) => {
  let errorMessage = {
    message: ''
  };
  let resourceNS = [];
  try {
    dispatch(showLoader);
    resourceNS = await getResourceNSApi();
  } catch (error: any) {
    errorMessage = {
      message: interpolateString([error], GET_ENTITIES_NAMESPACE_DATA_ERROR)
    };
  }
  finally {
    dispatch(setResourceNS(resourceNS));
    dispatch(hideLoader);
    dispatch(showErrorAction(errorMessage));
  }
}
/**
 * Get list of resources
 * @param dispatch - Disptach is used to dispatch the action to set the values
 */
export const getResources = async (payload: any, dispatch: Function) => {
  let errorMessage = {
    message: ''
  };
  let resources;
  try {
    dispatch(showLoader);
    resources = await getResourcesApi(payload);

  } catch (error: any) {
    errorMessage = {
      message: interpolateString([error], GET_ENTITIES_RESOURCE_DATA_ERROR)
    };
  }
  finally {
    dispatch(setResources(resources?.data, resources?.metadata));
    dispatch(hideLoader);
    dispatch(showErrorAction(errorMessage));
  }
}
/**
 * Get list of model versions
 * @param dispatch - Disptach is used to dispatch the action to set the values
 */
export const getDPModel = async (dispatch: Function) => {
  let errorMessage = {
    message: ''
  };
  let dpModel = { //Fallback model and version when model api is failed
    "model": "BYDM",
    "version": "2023.6.0",
    "default": true
  };
  try {
    dispatch(showLoader);
    dpModel = await getDPModelApi();
  } catch (error: any) {
    errorMessage = {
      message: interpolateString([error], GET_ENTITIES_DATA_MODEL_ERROR)
    };
  }
  finally {
    dispatch(setDPModel(dpModel));
    dispatch(hideLoader);
    dispatch(showErrorAction(errorMessage));
  }
}
/**
 * Recursive function to return flatten attribute based on the params
 * @param actualType - All the types from json schema
 * @param allDefinitions - All the definitions of the type
 * @param finalAttributes - List of attributes
 * @param keyName - Key of the attributes
 * @returns - Final attribute list
 */
function flattenAttributes(actualType: any, allDefinitions: any, finalAttributes: any, keyName: string) {
  for (const key in actualType.properties) { //Loop through to all the properties of type 


    const newKeyName = keyName ? `${keyName}.${key}` : key;   //Defines the custom key name


    const attribute = actualType.properties[key];     //Set the attribute


    if (!finalAttributes[newKeyName] && attribute['x-by-data-securable'])     //Set the property in attribute
      finalAttributes[newKeyName] = attribute;

    const ref = attribute['$ref'] || (attribute?.items && attribute?.items['$ref']) || (attribute?.allOf && attribute?.allOf[1] && attribute?.allOf[1]['$ref']) || '';
    if (ref) {
      try {
        const type = ref.split('/')[2];
        if (allDefinitions[type] && allDefinitions[type].properties && allDefinitions[type]['x-by-securable']) { //Check if type is present in all definition and has properties
          finalAttributes[newKeyName] = flattenAttributes(allDefinitions[type], allDefinitions, finalAttributes, newKeyName);    //Call the recursive function with new type object and key name
        }
        else {
          finalAttributes[newKeyName] = allDefinitions[type];
        }
      } catch (error: any) {
        console.log("Error in schema", key, attribute, attribute['$ref']);
      }
    }
  }
  return finalAttributes;
};


/**
 * Recursive function to return flatten attribute based on the params
 * @param actualType - All the types from json schema
 * @param allDefinitions - All the definitions of the type
 * @param finalAttributes - List of attributes
 * @param keyName - Key of the attributes
 * @returns - Final attribute list
 */
function getSecurableAttributesList(actualType: any, allDefinitions: any, finalAttributes: any, securablesList: any[]) {
  let newAttributes: any = {};
  securablesList.map((obj: any) => {
    const refPath = obj.refPath;
    const keyNameList = refPath.split('.');
    let prevAttribute = actualType;
    keyNameList.map((keyNameAtt: string) => {
      const attribute = prevAttribute.properties[removeQuestionMark(keyNameAtt)];
      if (attribute && attribute.$ref) {
        const type = attribute.$ref.split('/')[2];
        if (allDefinitions[type] && (allDefinitions[type].properties)) {
          prevAttribute = allDefinitions[type];
        } else if (allDefinitions[type].enum) {
          newAttributes[removeLocationNameFromAttribute(removeQuestionMark(refPath))] = allDefinitions[type];
        }
      } else {
        newAttributes[removeLocationNameFromAttribute(removeQuestionMark(refPath))] = attribute;
      }
    });
  });
  newAttributes = _.omitBy(newAttributes, _.isNil);
  _.forOwn(newAttributes, (attribute, key) => {
    if (attribute.type) {
      finalAttributes[key] = attribute;
    } else {
      const ref = attribute['$ref'] || attribute?.allOf && attribute?.allOf[1] && attribute?.allOf[1]['$ref'];
      const type = ref.split('/')[2];
      if (allDefinitions[type]) {
        finalAttributes[key] = allDefinitions[type];
      }
    }
  });
  return finalAttributes;
};

function removeQuestionMark(string: string) {
  return string.replace(/[?=]/g, "");
};

function removeLocationNameFromAttribute(string: string) {
  const newStr: any = string.split('.').slice(1);
  if (newStr?.length) {
    return newStr.join(".");
  }
  return newStr;
}

const getRemoteDataUrl = (attributeList: any) => {
  const remoteDataUrlObject = {};
  const allDefinitions = attributeList.definitions;
  let remoteUrl = {};
  const attributeType = attributeList.items['$ref'].split('/')[2];
  const actualType = allDefinitions[attributeType];
  if (actualType['x-by-securable'] && actualType['x-by-data-remote-url']) {
    remoteUrl = remoteUrlDataSpecifications(actualType, remoteDataUrlObject);
  }
  return remoteUrl;
}

const remoteUrlDataSpecifications = (actualType: any, remoteDataUrlObject: Object) => {
  const urlSpecifications = actualType.properties['x-by-data-remote-url-specification'];
  if (urlSpecifications) {
    remoteDataUrlObject['remoteDataUrl'] = urlSpecifications?.['remote-url-path'] || '';
    remoteDataUrlObject['attributeData'] = urlSpecifications.properties?.['remote-url-response-payload']?.['response-payload-data-fetch-pattern'];
    remoteDataUrlObject['paginationMethodValue'] = urlSpecifications.properties?.['remote-url-response-payload']?.['response-payload-pagination-method-fetch-pattern'];
    remoteDataUrlObject['totalCount'] = urlSpecifications.properties?.['remote-url-response-payload']?.['response-payload-total-count-fetch-pattern'];
    remoteDataUrlObject['paginationMethod'] = urlSpecifications.properties?.['remote-url-pagination-method']?.['pagination-method'];
  }
  return remoteDataUrlObject;
}

export async function getRemoteDataUrlResult(dispatch: Function, remoteUrlDataObject: any, payload: any,) {
  if (remoteUrlDataObject.paginationMethod === "offset") {
    payload.pageNo = (payload.pageNo - 1) * 10;
  }
  const remoteUrl = remoteUrlDataObject?.remoteDataUrl?.replaceAll('{SECURABLE-ENTITY-ATTRIBUTE}', payload.attribute).replaceAll('{REMOTE-URL-SEARCH-VALUE}', payload.searchString).replaceAll('{REMOTE-URL-PAGE-METHOD-VALUE}', payload.pageNo).replaceAll('{REMOTE-URL-PAGE-LIMIT-VALUE}', payload.pageSize);
  const attributeData = remoteUrlDataObject?.attributeData?.replaceAll('{SECURABLE-ENTITY-ATTRIBUTE}', payload.attribute);
  try {
    const remoteUrlDataResult = await getRemoteUrlDataResult(remoteUrl);
    const data = [...jp.query(remoteUrlDataResult, attributeData)].map(match => ({ id: match, displayName: match }));
    const pageMatch = jp.query(remoteUrlDataResult, remoteUrlDataObject?.paginationMethodValue)[0];
    let pageNo = pageMatch ? parseInt(pageMatch, 10) : 0;
    if (remoteUrlDataObject.paginationMethod === "offset") {
      pageNo = Math.floor(pageNo / 10) + 1;
    }
    const totalCountMatch = jp.query(remoteUrlDataResult, remoteUrlDataObject?.totalCount)[0];
    const totalCount = totalCountMatch ? parseInt(totalCountMatch, 10) : 0;
    dispatch(setRemoteDataAttributes(data, true, pageNo, totalCount));
  }
  catch (error: any) {
    dispatch(setRemoteDataAttributes([], false, 0, 0));
  }
}